import { ChevronRight, Lock } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Separator } from "@/shared/components/ui/separator";
import { cn } from "@/shared/lib/utils";
import {
  useAssistants,
  useContactAction,
  useContacts,
  useOpenConversation,
} from "../api/use-directory";
import { assistantName } from "../lib/assistant-name";
import { lockedAssistant, lockedMessage } from "../lib/handler-lock";
import type { Identity, Session } from "../types";
import { Avatar } from "./avatar";
import { BottomSheet } from "./bottom-sheet";
import { ERROR_CLASS, HOVER_CARD } from "./styles";

const ROW_CLASS =
  "flex h-auto min-h-11 w-full items-center justify-start gap-3 rounded-none px-0 py-2.5 text-left font-normal whitespace-normal";

const ROW_BUTTON_CLASS = cn("cursor-pointer", HOVER_CARD);

const NAME_CLASS = "block truncate text-base font-medium";

const DETAIL_CLASS = "block truncate text-sm text-muted-foreground";

const STATUS_CLASS = "py-1 text-xs text-muted-foreground";

const ACTION_CLASS =
  "min-h-11 cursor-pointer text-xs text-gold-soft motion-reduce:transition-none";

export function ContactsSheet({
  identity,
  session,
  onClose,
  onSelect,
}: {
  identity: Identity;
  session: Session;
  onClose: () => void;
  onSelect: (chatId: string) => void;
}) {
  const contacts = useContacts(identity);
  const contactAction = useContactAction(identity);
  const openConversation = useOpenConversation(identity);
  const assistants = useAssistants(identity).data?.assistants;
  const locked = lockedAssistant(assistants, session.assistant.memberId);
  const name = assistantName(undefined, assistants, session);
  const lockedText = locked ? lockedMessage(locked, name) : undefined;
  const open = (peerId: string) =>
    openConversation.mutate(peerId, {
      onSuccess: ({ chatId }) => onSelect(chatId),
    });
  return (
    <BottomSheet
      title="Contacts"
      description="Start a private conversation or manage contact requests."
      onClose={onClose}
    >
      <Button
        variant="ghost"
        className={cn(ROW_CLASS, ROW_BUTTON_CLASS)}
        onClick={() => open(session.assistant.memberId)}
        disabled={openConversation.isPending || Boolean(locked)}
      >
        <Avatar name={name} ai />
        <span className="min-w-0 flex-1">
          <strong className={NAME_CLASS}>{name}</strong>{" "}
          <small className={DETAIL_CLASS} title={lockedText}>
            {lockedText ?? "Your handler"}
          </small>
        </span>
        {locked ? (
          <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        ) : (
          <ChevronRight className="size-4 text-muted-foreground" />
        )}
      </Button>
      <Separator className="bg-border/60" />
      {(contacts.data?.directory ?? contacts.data?.contacts ?? []).map(
        (contact) => {
          const status = contacts.data?.directory?.find(
            (member) => member.memberId === contact.memberId,
          );
          const accepted =
            !status ||
            ((status.status === "ACCEPTED" || status.status === "CONNECTED") &&
              status.eligible);
          const stateLabel = accepted
            ? "Accepted contact"
            : status?.status === "REQUESTED"
              ? status.direction === "INCOMING"
                ? "Wants to connect"
                : "Request sent"
              : status?.status === "DECLINED"
                ? "Request declined"
                : "Not connected";
          return (
            <div key={contact.memberId}>
              {accepted ? (
                <Button
                  variant="ghost"
                  className={cn(ROW_CLASS, ROW_BUTTON_CLASS)}
                  onClick={() => open(contact.memberId)}
                  disabled={openConversation.isPending}
                >
                  <Avatar name={contact.name} size="md" />
                  <span className="min-w-0 flex-1">
                    <strong className={NAME_CLASS}>{contact.name}</strong>{" "}
                    <small className={DETAIL_CLASS}>{stateLabel}</small>
                  </span>
                  <ChevronRight className="size-4 text-muted-foreground" />
                </Button>
              ) : (
                <div className={cn(ROW_CLASS, "text-sm")}>
                  <Avatar name={contact.name} size="md" />
                  <span className="min-w-0 flex-1">
                    <strong className={NAME_CLASS}>{contact.name}</strong>{" "}
                    <small className={DETAIL_CLASS}>{stateLabel}</small>
                  </span>
                </div>
              )}
              {status?.eligibilityReason && (
                <p className={STATUS_CLASS}>{status.eligibilityReason}</p>
              )}
              {status?.guardianStatus === "UNVERIFIED" && (
                <p className={STATUS_CLASS}>
                  Guardian approval has not been verified.
                </p>
              )}
              {(status?.canRequest || status?.canRespond) && (
                <div className="flex justify-end gap-2 py-1.5">
                  {status.canRequest && (
                    <Button
                      variant="outline"
                      size="sm"
                      className={ACTION_CLASS}
                      disabled={contactAction.isPending}
                      onClick={() =>
                        contactAction.mutate({
                          memberId: contact.memberId,
                          action: "REQUEST",
                        })
                      }
                    >
                      Request contact
                    </Button>
                  )}
                  {status.canRespond && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className={ACTION_CLASS}
                        disabled={contactAction.isPending || !status.eligible}
                        onClick={() =>
                          contactAction.mutate({
                            memberId: contact.memberId,
                            action: "ACCEPT",
                          })
                        }
                      >
                        Accept
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className={ACTION_CLASS}
                        disabled={contactAction.isPending}
                        onClick={() =>
                          contactAction.mutate({
                            memberId: contact.memberId,
                            action: "DECLINE",
                          })
                        }
                      >
                        Decline
                      </Button>
                    </>
                  )}
                </div>
              )}
              <Separator className="bg-border/60" />
            </div>
          );
        },
      )}
      {contactAction.error && (
        <p className={ERROR_CLASS} role="alert">
          {contactAction.error.message}
        </p>
      )}
      {contacts.isPending && (
        <p className="p-3 text-center text-xs leading-[1.7] text-muted-foreground">
          Loading contacts…
        </p>
      )}
      {contacts.error && (
        <p className={ERROR_CLASS} role="alert">
          {contacts.error.message}
          <Button
            variant="ghost"
            size="sm"
            className="min-h-11 cursor-pointer motion-reduce:transition-none"
            onClick={() => void contacts.refetch()}
          >
            Retry
          </Button>
        </p>
      )}

      {openConversation.error && (
        <p className={ERROR_CLASS} role="alert">
          {openConversation.error.message}
        </p>
      )}
    </BottomSheet>
  );
}
